import React, { useState } from "react";
import { PassingNetwork, Unavailable } from "./FootballPitch";
import { defaultEdgeThreshold, networkPairs } from "./fiData";
import "./fi-extra.css";

/**
 * Passing network with a pass-count threshold, player highlight and the
 * strongest links in a table. Networks cover the period until the first
 * substitution only, which the component repeats wherever totals are shown.
 */
export default function NetworkExplorer({ networks = [], matchName = () => "", sampleSize, hideCoverage = false }) {
  const [index, setIndex] = useState(0);

  if (!networks.length) {
    return (
      <Unavailable
        text={`No passing networks exist for the ${sampleSize ?? "selected"} sampled matches. Networks are cached for only a small number of matches — try "Full cached season" in the sample filter.`}
      />
    );
  }

  const safeIndex = Math.min(index, networks.length - 1);
  return (
    <div>
      <div className="fx-toolbar">
        {networks.length > 1 && (
          <label className="fx-field">Match
            <select value={safeIndex} onChange={(e) => setIndex(Number(e.target.value))} aria-label="Network match">
              {networks.map((n, i) => (
                <option key={`${n.fixture_id}-${i}`} value={i}>{matchName(n.fixture_id) || n.fixture_id}</option>
              ))}
            </select>
          </label>
        )}
        {!hideCoverage && <span className="fx-coverage">
          Network available for <b>{networks.length}</b> of <b>{sampleSize ?? "?"}</b> sampled matches
        </span>}
      </div>
      <NetworkBody key={safeIndex} network={networks[safeIndex]} />
    </div>
  );
}

function NetworkBody({ network }) {
  const pairs = networkPairs(network);
  const strongest = pairs[0]?.total || 1;
  const [threshold, setThreshold] = useState(defaultEdgeThreshold({ edges: pairs.map((p) => ({ passes: p.total })) }));
  const [highlight, setHighlight] = useState(null);

  const visible = pairs.filter((p) => p.total >= threshold).length;
  const nodes = [...(network.nodes || [])].sort((a, b) => (b.passes || 0) - (a.passes || 0));

  return (
    <div className="fx-two" style={{ margin: 0 }}>
      <div>
        <div className="fx-toolbar">
          <label className="fx-field">Minimum passes per pair: {threshold}
            <input
              type="range"
              min="1"
              max={strongest}
              value={Math.min(threshold, strongest)}
              onChange={(e) => setThreshold(Number(e.target.value))}
              aria-label="Minimum passes per pair"
            />
          </label>
          <span className="fx-coverage"><b>{visible}</b> of <b>{pairs.length}</b> pairs drawn</span>
          {highlight && <button type="button" className="fx-button" onClick={() => setHighlight(null)}>Clear highlight</button>}
        </div>
        <PassingNetwork network={network} minPasses={threshold} highlightId={highlight} onHighlight={setHighlight} />
      </div>

      <div className="fx-stack">
        <div>
          <h4 style={{ margin: "0 0 8px", fontSize: 13 }}>Strongest pairs</h4>
          <div className="fx-scroll" style={{ maxHeight: 250 }}>
            <table>
              <thead><tr><th>Pair</th><th className="fx-num">Passes</th></tr></thead>
              <tbody>
                {pairs.slice(0, 8).map((p) => (
                  <tr key={p.key}>
                    <td>{p.a.name} ↔ {p.b.name}<small>{p.a.name.split(" ").pop()} → {p.b.name.split(" ").pop()} {p.ab} · back {p.ba}</small></td>
                    <td className="fx-num">{p.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h4 style={{ margin: "0 0 8px", fontSize: 13 }}>Players</h4>
          <div className="fx-scroll" style={{ maxHeight: 300 }}>
            <table>
              <thead><tr><th>Player</th><th className="fx-num">Passes</th><th className="fx-num">Received</th></tr></thead>
              <tbody>
                {nodes.map((n) => (
                  <tr key={n.player.id} onClick={() => setHighlight(n.player.id === highlight ? null : n.player.id)} style={{ cursor: "pointer", background: n.player.id === highlight ? "var(--fx-surface-2)" : undefined }}>
                    <td>{n.player.shirt_number != null ? `${n.player.shirt_number} · ` : ""}{n.player.name}</td>
                    <td className="fx-num">{n.passes}</td>
                    <td className="fx-num">{n.passes_received}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {typeof network.centralization === "number" && (
          <p className="fi-note">Provider centralization index {network.centralization.toFixed(2)} (provider-defined). Lines combine both passing directions between two players.</p>
        )}
      </div>
    </div>
  );
}
